import { FormProvider, yupResolver } from '@slal/ui/react-hook-form/core';
import { ComboBoxField, Field, FieldSet, TextField } from '@slal/ui/react-hook-form/fields';
import { normalizeNationalInsuranceNumber, normalizePostcode } from '@slal/ui/react-hook-form/utils';
import { useNavigate } from 'react-router';
import { MaxWidthContent, StyledForm } from '~/components';
import { useApplicationForm, useNormalizations } from '~/hooks';
import { additionalDetailsSchema } from '~/schemas';
import { AdditionalDetailsSchema, ApplicationFormKeys } from '~/types';
import { ApplicationButtons } from '../ApplicationButtons';
import { employmentStatusOptions, initialValues, maritalStatusOptions } from './utils';

export const AdditionalDetailsFieldSet = () => {
    const { normalizeNationalInsuranceNumber: normalizeNin, normalizePostcode: normalizePostCode } = useNormalizations({
        normalizeNationalInsuranceNumber,
        normalizePostcode,
    });

    return (
        <FieldSet aria-label="A few more details">
            <Field
                label="National Insurance number"
                labelFor="personalInformation.nationalInsuranceNumber"
                helpMessage="For example QQ123456C"
            >
                <TextField
                    name="personalInformation.nationalInsuranceNumber"
                    id="personalInformation.nationalInsuranceNumber"
                    setValueAs={normalizeNin}
                />
            </Field>
            <Field label="Employment status" labelFor="personalInformation.employmentStatus">
                <ComboBoxField
                    name="personalInformation.employmentStatus"
                    id="personalInformation.employmentStatus"
                    options={employmentStatusOptions}
                />
            </Field>
            <Field label="Marital status" labelFor="personalInformation.maritalStatus">
                <ComboBoxField
                    name="personalInformation.maritalStatus"
                    id="personalInformation.maritalStatus"
                    options={maritalStatusOptions}
                />
            </Field>
            <Field
                label="Address line 1"
                labelFor="personalInformation.addressLineOne"
                helpMessage="House name/number and street"
            >
                <TextField name="personalInformation.addressLineOne" />
            </Field>
            <Field label="Address line 2" labelFor="personalInformation.addressLineTwo">
                <TextField name="personalInformation.addressLineTwo" />
            </Field>
            <Field label="Address line 3 (optional)" labelFor="personalInformation.addressLineThree">
                <TextField name="personalInformation.addressLineThree" />
            </Field>
            <Field label="Postcode" labelFor="personalInformation.postcode">
                <TextField name="personalInformation.postcode" setValueAs={normalizePostCode} />
            </Field>
        </FieldSet>
    );
};

export const AdditionalDetailsForm = () => {
    const { handleSubmit, form } = useApplicationForm<AdditionalDetailsSchema>(
        {
            defaultValues: initialValues,
            resolver: yupResolver(additionalDetailsSchema),
        },
        ApplicationFormKeys.PERSONAL_INFORMATION
    );

    const navigate = useNavigate();
    const onValidCallback = () => navigate('/application/pensions');

    return (
        <MaxWidthContent>
            <FormProvider {...form}>
                <StyledForm onSubmit={handleSubmit(onValidCallback)}>
                    <AdditionalDetailsFieldSet />
                    <ApplicationButtons backTo="/application/personal-information" />
                </StyledForm>
            </FormProvider>
        </MaxWidthContent>
    );
};
